import { Virtualizer, observeElementOffset, observeElementRect, elementScroll } from '@tanstack/virtual-core';
export class VirtualRows {
    constructor(host, opts) {
        this.count = 0;
        /**
         * Per-row ref for MEASURED mode: reports the row's real height back to the virtualiser.
         *
         * The element must carry `data-index` — that is how virtual-core knows which row it just
         * measured, and without it the measurement is silently attributed to nothing.
         */
        this.measure = (el) => {
            if (!el || !this.opts.measured)
                return;
            this.virtualizer?.measureElement(el);
        };
        /** Called from the template's ref: the scroll container has appeared (or gone away on re-render). */
        this.attach = (el) => {
            const next = el;
            if (next === this.scroller)
                return;
            this.scroller = next;
            this.virtualizer = undefined;
            if (next)
                this.ensure();
        };
        this.host = host;
        this.opts = { overscan: 8, measured: false, ...opts };
        host.addController(this);
    }
    hostDisconnected() {
        this.virtualizer?._willUpdate();
        this.virtualizer = undefined;
        this.scroller = undefined;
    }
    /** The list changed length. Kept separate from attach so a data update does not rebuild the scroller. */
    setCount(count) {
        if (count === this.count)
            return;
        this.count = count;
        this.virtualizer?.setOptions({ ...this.virtualizer.options, count });
        this.virtualizer?.measure();
        this.host.requestUpdate();
    }
    /** Total scrollable height — the spacer that makes the scrollbar reflect the WHOLE list. */
    get totalSize() {
        return this.virtualizer?.getTotalSize() ?? this.count * this.opts.estimateSize;
    }
    /**
     * The rows to build right now.
     *
     * Before the scroller exists there is no measurement to window by, so this returns the first
     * screenful rather than nothing: a first paint of zero rows followed by a jump to full is the
     * layout shift these panels are built to avoid.
     */
    get rows() {
        if (this.virtualizer) {
            const items = this.virtualizer.getVirtualItems();
            /*
             * AN ATTACHED VIRTUALISER CAN STILL KNOW NOTHING.
             *
             * On the first paint the scroll element exists but has not been laid out, so its
             * rect is 0×0 and the window it computes is empty — and because nothing scrolls
             * or resizes afterwards on a short list, `onChange` never fires to correct it.
             * The result was a spacer sized for the whole log with no rows inside it: the
             * panel looked empty while claiming to be full.
             *
             * So an empty window over a non-empty list falls through to the seed below
             * rather than being trusted. Windowing is an optimisation; showing the rows is
             * the job, and the optimisation never gets to win that argument.
             */
            if (items.length > 0 || this.count === 0) {
                return items.map((v) => ({ index: v.index, start: v.start, size: v.size }));
            }
        }
        const seed = Math.min(this.count, this.opts.overscan * 2);
        return Array.from({ length: seed }, (_, i) => ({
            index: i,
            start: i * this.opts.estimateSize,
            size: this.opts.estimateSize,
        }));
    }
    ensure() {
        const scroller = this.scroller;
        if (!scroller || this.virtualizer)
            return;
        this.virtualizer = new Virtualizer({
            count: this.count,
            getScrollElement: () => scroller,
            estimateSize: () => this.opts.estimateSize,
            overscan: this.opts.overscan,
            // Only in measured mode: virtual-core reads the element's box and remembers it per index.
            ...(this.opts.measured
                ? { measureElement: (el) => el.getBoundingClientRect().height }
                : {}),
            observeElementOffset,
            observeElementRect,
            scrollToFn: elementScroll,
            onChange: () => this.host.requestUpdate(),
        });
        this.virtualizer._willUpdate();
        this.virtualizer.measure();
        this.host.requestUpdate();
        /* The rect is only real after layout, so measure again on the next frame — this is
         * what turns the seeded first paint into a properly windowed second one. */
        if (typeof requestAnimationFrame !== 'function')
            return;
        requestAnimationFrame(() => {
            if (!this.virtualizer || this.scroller !== scroller)
                return;
            this.virtualizer._willUpdate();
            this.virtualizer.measure();
            this.host.requestUpdate();
        });
    }
}
//# sourceMappingURL=virtual-rows.js.map